import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses.js';
import { customerMsg, nameOfCatalogMsg, selectedApproverMsg, statusMsg, t } from '../../common/i18n/index.js';
import { getPriceAsText } from '../../common/utils/priceUtils';
import { DeliveryOrder } from '../../generated/api/models.js';
import { Status } from '../Status/Status.js';
import { getCustomerOrderStatusInfo, orderContainsBillingPeriod } from './CustomerOrderDetailsUtil.js';
import type { CLDescriptionItem } from '../../common/types/componentLibrary';
import type { CustomerOrder } from '../../generated/api/models.js';

interface CustomerOrderInfoParams {
  customerOrder: CustomerOrder;
  isApprover: boolean;
}

export const CustomerOrderInfo = ({ customerOrder, isApprover }: CustomerOrderInfoParams) => {
  const customerOrderStatusInfo = getCustomerOrderStatusInfo(
    customerOrder.status ? customerOrder.status.toString() : undefined
  );
  const isBillingPeriodOrder = orderContainsBillingPeriod(customerOrder);

  const recurringChargesTitle = isBillingPeriodOrder
    ? t.ORQE('Recurring charges, VAT 0%')
    : t.E0K4('Total monthly charges, VAT 0%');

  const fieldsLeft: CLDescriptionItem[] = [
    {
      title: t.ASQT(statusMsg),
      description: <Status color={customerOrderStatusInfo.color} textAfterBadge={customerOrderStatusInfo.text} />,
    },
  ];

  if (customerOrder.customerOrderContact !== undefined) {
    fieldsLeft.push({
      title: t.IY5V(customerMsg),
      description: customerOrder.customerOrderContact,
    });
  }

  if (customerOrder.selectedApprover !== undefined) {
    fieldsLeft.push({
      title: t.GOZ7(selectedApproverMsg),
      description: customerOrder.selectedApprover,
    });
  }

  if (customerOrder.catalogName !== undefined) {
    fieldsLeft.push({
      title: t.Z0HF(nameOfCatalogMsg),
      description: customerOrder.catalogName || undefined,
    });
  }

  const deliveryFeeOrders = customerOrder.deliveryOrders.filter(
    deliveryOrder => deliveryOrder.productType === DeliveryOrder.ProductTypeEnum.DELIVERY_FEE
  );

  const deliveryFeeOrdersWithOneTimeCharge = deliveryFeeOrders.filter(
    deliveryOrder => deliveryOrder.oneTimeCharge != null
  );

  const deliveryFees = deliveryFeeOrdersWithOneTimeCharge.length
    ? deliveryFeeOrdersWithOneTimeCharge.reduce((sum, current) => sum + current.oneTimeCharge!, 0)
    : undefined;

  const deliveryFeeOrdersWithOneTimeListCharge = deliveryFeeOrders.filter(
    deliveryOrder => deliveryOrder.oneTimeListCharge != null
  );

  const deliveryListFees = deliveryFeeOrdersWithOneTimeListCharge.length
    ? deliveryFeeOrdersWithOneTimeListCharge.reduce((sum, current) => sum + current.oneTimeListCharge!, 0)
    : undefined;

  const fieldsRight: CLDescriptionItem[] = [
    {
      title: recurringChargesTitle,
      description: getPriceAsText(
        customerOrder.totalMonthlyRecurringCharge,
        customerOrder.totalMonthlyRecurringListCharge,
        !isBillingPeriodOrder
      ),
    },
    {
      title: t.TE4L('Total one-time payments'),
      description: getPriceAsText(customerOrder.totalOneTimeCharge, customerOrder.totalOneTimeListCharge),
    },
    {
      title: t.PFDS('Delivery fee'),
      description: getPriceAsText(deliveryFees, deliveryListFees) ?? '—',
    },
  ];

  const visibleFields = isApprover ? fieldsLeft.concat(fieldsRight) : fieldsLeft;

  return <CL.Description items={visibleFields} className={dsClass.PADDING_BOTTOM_5} />;
};
